import { isWorldModelShadowEnabled } from "../../world-model/mode";
import { apiUrl as defaultApiUrl, fetchJson as defaultFetchJson } from "../../utils/api";
import {
  buildVoiceSceneSignature,
  isGroundedVoiceQuestion,
  normalizeGroundedVoiceAnswer,
} from "./voicePerception";
import { VOICE_RETRY_MESSAGE } from "./voiceMessages";

const VOICE_PERCEPTION_RETRY_MESSAGE =
  "I couldn't verify the current scene. Please try that question again.";

function normalizeUnresolvedBackendResult(result = {}) {
  const action = String(result?.action || "").trim().toLowerCase();
  const untrustedUtilityAction = ["contact_joz", "call_joz"].includes(action);

  if ((result?.action || result?.target) && !untrustedUtilityAction) return result;

  return {
    ...result,
    action: null,
    target: null,
    status: "needs_retry",
    awareness: VOICE_RETRY_MESSAGE,
  };
}

async function attachShadowPrediction({
  rawInput,
  context,
  fetchJson,
  apiUrl,
  localResult,
}) {
  const requestJson = typeof fetchJson === "function" ? fetchJson : defaultFetchJson;
  const buildApiUrl = typeof apiUrl === "function" ? apiUrl : defaultApiUrl;
  const isLocalDevelopment =
    typeof window !== "undefined" &&
    /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname) &&
    process.env.NODE_ENV !== "production";

  if (
    (!isWorldModelShadowEnabled() && !isLocalDevelopment) ||
    typeof requestJson !== "function" ||
    typeof buildApiUrl !== "function"
  ) {
    return localResult;
  }

  try {
    const agenticResult = await requestJson(buildApiUrl("/api/agentic"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        input: rawInput,
        context,
      }),
    });

    return {
      ...localResult,
      prediction: agenticResult?.prediction || null,
    };
  } catch (error) {
    console.warn("⚠️ Shadow prediction enrichment unavailable; continuing locally:", error?.message || error);
    return localResult;
  }
}

async function resolveBackendSpatialIntent({
  rawInput,
  context,
  fetchJson,
  apiUrl,
}) {
  const requestJson = typeof fetchJson === "function" ? fetchJson : defaultFetchJson;
  const buildApiUrl = typeof apiUrl === "function" ? apiUrl : defaultApiUrl;
  try {
    const payload = await requestJson(buildApiUrl("/api/world-model/spatial-intent"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        input: rawInput,
        context,
      }),
    });
    if (!payload?.matched || !payload?.placement?.entitySet) return null;
    return {
      action: payload.placement.action || "experience_spatially",
      target: null,
      awareness: payload.placement.action === "experience_spatially"
        ? "I’ll open a safe spatial AI experience."
        : "I’ll prepare a safe spatial placement preview for your confirmation.",
      placement: payload.placement,
      semanticIntent: {
        source: payload.source,
        confidence: payload.confidence,
        modelRuntime: payload.modelRuntime,
      },
    };
  } catch (error) {
    console.warn("⚠️ Semantic spatial intent unavailable:", error?.message || error);
    return null;
  }
}

async function resolveGroundedVoiceQuestion({
  rawInput,
  isMobile,
  currentPortal,
  currentMesh,
  currentMeshStage,
  context,
  fetchJson,
  apiUrl,
}) {
  const requestJson = typeof fetchJson === "function" ? fetchJson : defaultFetchJson;
  const buildApiUrl = typeof apiUrl === "function" ? apiUrl : defaultApiUrl;
  const requestContext = {
    ...(context || {}),
    currentPortal,
    currentMesh,
    currentMeshStage,
    interactionChannel: "voice",
    uiState: {
      ...(context?.uiState || {}),
      isMobile: Boolean(isMobile),
    },
  };
  const conversationId = String(context?.conversationId || "").trim() || null;
  const sessionKey = String(context?.sessionKey || context?.session_key || "").trim() || null;
  const sceneSignature = buildVoiceSceneSignature({
    currentPortal,
    currentMesh,
    currentMeshStage,
    context: requestContext,
  });

  try {
    const payload = await requestJson(buildApiUrl("/api/joz-llm"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        conversationId: conversationId || undefined,
        sessionKey: sessionKey || undefined,
        messages: [{ role: "user", content: rawInput }],
        context: requestContext,
      }),
    });
    const result = normalizeGroundedVoiceAnswer(payload);
    if (result) return {
      result,
      sceneSignature,
      conversationId: result.conversationId || payload?.conversationId || conversationId,
    };
  } catch (error) {
    console.warn("⚠️ Grounded Voice MAXX answer unavailable:", error?.message || error);
  }

  return {
    sceneSignature,
    result: {
      action: null,
      target: null,
      status: "needs_retry",
      awareness: VOICE_PERCEPTION_RETRY_MESSAGE,
      spokenReply: VOICE_PERCEPTION_RETRY_MESSAGE,
    },
    conversationId,
  };
}

export async function resolveVoicePipeline({
  rawInput,
  isMobile,
  currentPortal,
  currentMesh,
  currentMeshStage,
  context,
  detectImmediateMobileCommand,
  resolveLocalVoiceCommand,
  fetchJson,
  apiUrl,
}) {
  const rawLower = String(rawInput || "").trim().toLowerCase();
  const mobileShortcut = isMobile ? detectImmediateMobileCommand(rawLower) : null;
  const spoken = mobileShortcut || rawLower;

  if (!spoken) {
    return {
      rawLower,
      mobileShortcut,
      spoken: "",
      source: null,
      result: null,
      backendMode: null,
    };
  }

  const localResult = resolveLocalVoiceCommand(
    spoken,
    currentPortal,
    currentMesh,
    currentMeshStage
  );

  if (localResult) {
    const enrichedLocalResult = await attachShadowPrediction({
      rawInput: spoken,
      context,
      fetchJson,
      apiUrl,
      localResult,
    });
    return {
      rawLower,
      mobileShortcut,
      spoken,
      source: "local",
      result: enrichedLocalResult,
      backendMode: null,
    };
  }

  if (isGroundedVoiceQuestion(spoken)) {
    const grounded = await resolveGroundedVoiceQuestion({
      rawInput: spoken,
      isMobile,
      currentPortal,
      currentMesh,
      currentMeshStage,
      context,
      fetchJson,
      apiUrl,
    });
    return {
      rawLower,
      mobileShortcut,
      spoken,
      source: "backend",
      backendMode: "joz_llm_grounded",
      sceneSignature: grounded.sceneSignature,
      conversationId: grounded.conversationId || null,
      result: grounded.result,
    };
  }

  const semanticSpatialResult = await resolveBackendSpatialIntent({
    rawInput: spoken,
    context,
    fetchJson,
    apiUrl,
  });
  if (semanticSpatialResult) {
    const enrichedSemanticResult = await attachShadowPrediction({
      rawInput: spoken,
      context,
      fetchJson,
      apiUrl,
      localResult: semanticSpatialResult,
    });
    return {
      rawLower,
      mobileShortcut,
      spoken,
      source: "backend",
      backendMode: "semantic_spatial_intent",
      result: enrichedSemanticResult,
    };
  }

  const agenticResult = await fetchJson(apiUrl("/api/agentic"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      input: spoken,
      context,
      sessionKey: context?.sessionKey || context?.session_key || undefined,
      interactionChannel: "voice",
    }),
  });

  const agenticParams = agenticResult?.params || {};
  const hasAgenticAction =
    Boolean(agenticParams.action) ||
    Boolean(agenticParams.target) ||
    Boolean(agenticParams.awareness);

  if (hasAgenticAction) {
    return {
      rawLower,
      mobileShortcut,
      spoken,
      source: "backend",
      backendMode: "agentic",
      result: normalizeUnresolvedBackendResult({
        action: agenticParams.action || null,
        target: agenticParams.target || null,
        awareness: agenticParams.awareness || agenticResult?.response || null,
        source: agenticParams.source || "agentic",
        prediction: agenticResult?.prediction || null,
      }),
    };
  }

  const thinkResult = await fetchJson(apiUrl("/api/think"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      transcript: spoken,
      currentPortal,
      currentMesh,
      currentMeshStage,
      agentContext: context,
      app_context: context?.app_context || null,
      sessionKey: context?.sessionKey || context?.session_key || undefined,
      conversationId: context?.conversationId || undefined,
      interactionChannel: "voice",
    }),
  });

  return {
    rawLower,
    mobileShortcut,
    spoken,
    source: "backend",
    backendMode: "think_fallback",
    result: normalizeUnresolvedBackendResult({
      action: thinkResult?.action || null,
      target: thinkResult?.target || null,
      awareness: thinkResult?.awareness || null,
      timing: thinkResult?.timing || null,
    }),
  };
}
